import React, { FC } from 'react';

import { Button, Form, Layout, message, Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { FormSkeleton } from '@components/FormSkeleton';
import { PageTitle } from '@components/PageTitle';

import { FormCatalog, IFormValues } from '../../components/Form';
import {
  useGetCatalogAllProductsQuery,
  useGetCatalogQuery,
  useGetCatalogSettingsQuery,
  useUpdateCatalogQuery,
} from '../../hooks';
import { ProductModalProvider } from '../../providers';

export const Edit: FC = () => {
  const params = useParams<{ id: string }>();
  const commonT = useTranslation(NSKeys.common);
  const catalogsT = useTranslation(NSKeys.catalogs);
  const validationT = useTranslation(NSKeys.validation);
  const navigate = useNavigate();
  const [form] = Form.useForm<IFormValues>();
  const goBack = () => navigate('/dashboard/catalogs');
  const { data: catalog, isLoading: isCatalogLoading, isError: isCatalogError } = useGetCatalogQuery(params.id || '');
  const {
    data: settings,
    isLoading: settingsLoading,
    isError: isSettingsError,
  } = useGetCatalogSettingsQuery(params.id);
  const { mutate: updateCatalog, status: updateStatus, failureReason } = useUpdateCatalogQuery();
  const { data: products, isLoading: isProductsLoading } = useGetCatalogAllProductsQuery({ catalog_uid: params.id });

  React.useEffect(() => {
    if (isCatalogError || isSettingsError) {
      goBack();
      message.error(catalogsT.t('errors.notFound'));
    }
  }, [isCatalogError, isSettingsError]);

  React.useEffect(() => {
    if (updateStatus === 'success') {
      goBack();
    }
  }, [updateStatus]);

  React.useEffect(() => {
    if (failureReason) {
      const formValues = form.getFieldsValue();
      const { pointerErrors } = getFormErrorsFromResponse(formValues, failureReason, validationT.t);
      if (pointerErrors.length) {
        form.setFields(pointerErrors);
      }
    }
  }, [failureReason]);

  const handleSubmit = async (formValues: IFormValues): Promise<void> => {
    const uids = formValues.media?.map((el) => el?.response?.uid);

    updateCatalog({
      id: params.id || '',
      data: {
        name: formValues.name,
        start_date: formValues.start_date || null,
        end_date: formValues.end_date || null,
        process_uid: formValues?.process_uid || null,
        warehouse_uid: formValues?.warehouse?.value || null,
        project_uid: formValues?.project_uid ? formValues.project_uid : null,
        cover_uid: uids?.[0] || catalog?.cover?.uid,
        ...(formValues?.warehouse?.value && { product_uids: formValues?.product_uids || [] }),
        settings: {
          visibility_rule: formValues.visibility_rule,
          ...(formValues?.warehouse?.value && { catalog_type: formValues.catalog_type }),
          order_approval_rule: formValues.order_approval_rule,
          order_approval_chain_uid: formValues.order_approval_chain_uid,
          show_multiplicity_indicator: formValues.show_multiplicity_indicator,
          ...(formValues.visible_to_users?.length && {
            visible_to_user_ids: formValues.visible_to_users.map((u) => u.value).filter((u): u is number => true),
          }),
          ...(formValues.visible_to_user_group_uids?.length && {
            visible_to_user_group_uids: formValues.visible_to_user_group_uids,
          }),
        },
      },
    });
  };

  return (
    <>
      <Layout.Header>
        <Breadcrumbs />
        <Row justify="space-between" align="middle">
          <PageTitle title={catalogsT.t('titleEdit')} onClick={goBack} />
          <Space size="middle">
            <Button
              disabled={updateStatus === 'loading'}
              type="primary"
              size="middle"
              form="catalogForm"
              htmlType="submit">
              {commonT.t('buttonSave')}
            </Button>
            <Button onClick={goBack} size="middle">
              {commonT.t('buttonCancel')}
            </Button>
          </Space>
        </Row>
      </Layout.Header>
      {isCatalogLoading || settingsLoading || isProductsLoading ? (
        <FormSkeleton />
      ) : (
        <ProductModalProvider>
          <FormCatalog
            form={form}
            onFinish={handleSubmit}
            areDateFieldsDisabled={!catalog?.start_date}
            initial_user_group_options={settings?.visible_to_user_groups?.map((el) => ({
              label: el.name,
              value: el.uid,
            }))}
            initial_order_approval_chain_options={[
              { value: settings?.order_approval_chain?.uid || '', label: settings?.order_approval_chain?.name || '' },
            ]}
            initialProducts={products?.items}
            initialValues={{
              name: catalog?.name || '',
              start_date: catalog?.start_date || null,
              end_date: catalog?.end_date || null,
              warehouse: catalog?.warehouse ? { value: catalog.warehouse.uid, label: catalog.warehouse.name } : null,
              visibility_rule: settings?.visibility_rule,
              catalog_type: settings?.catalog_type || undefined,
              order_approval_rule: settings?.order_approval_rule,
              show_multiplicity_indicator: settings?.show_multiplicity_indicator,
              product_uids: products?.items?.map((el) => el.uid),
              project_uid: catalog?.project?.uid,
              visible_to_users: (settings?.visible_to_users ?? []).map((u) => ({
                value: u.id,
                label: u.name,
              })),
              visible_to_user_group_uids: settings?.visible_to_user_groups?.map((el) => el.uid),
              order_approval_chain_uid: settings?.order_approval_chain?.uid,
              process_uid: catalog?.process?.uid,
              media: [{ uid: catalog?.cover?.uid, status: 'done', url: catalog?.cover?.variants?.thumbnail?.url }],
            }}
          />
        </ProductModalProvider>
      )}
    </>
  );
};
